import { ChangeDetectionStrategy, Component, effect, input, model } from '@angular/core';
import { Mask } from '../../interfaces';

@Component({
    selector: 'ca-numeric-stepper',
    templateUrl: './numeric-stepper.component.html',
    styleUrls: ['./numeric-stepper.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NumericStepperComponent {
    private readonly defaultNumericMin = 0;
    private readonly defaultNumericMax = 99;
    private readonly defaultNumericStep = 1;
    numericMin = input<number, number | undefined>(this.defaultNumericMin, {
        transform: value => value ?? this.defaultNumericMin
    });
    numericMax = input<number, number | undefined>(this.defaultNumericMax, {
        transform: value => value ?? this.defaultNumericMax
    });
    numericStep = input<number, number | undefined>(this.defaultNumericStep, {
        transform: value => value ?? this.defaultNumericStep
    });
    numericMask = input<Mask>();
    value = model<number>();
    title = input<string>();
    disabled = input<boolean>(false);
    errorMessage = input<string | null>(null);
    step(delta: number): void {
        if (this.disabled()) {
            return;
        }

        this.value.update(current => this.normalizeValue((current ?? 0) + delta * this.numericStep()));
    }

    onInputChange(event: Event): void {
        if (this.disabled()) {
            return;
        }

        const target = event.target as HTMLInputElement | null;
        if (!target) {
            return;
        }

        const parsed = Number(target?.value ?? '');
        if (Number.isNaN(parsed)) {
            return;
        }

        this.value.set(this.normalizeValue(parsed));
        // ensure input value stays clamped
        target.value = this.value()?.toString() ?? '';
    }

    private clampValue(value: number): number {
        return Math.max(this.numericMin(), Math.min(this.numericMax(), value));
    }

    /**
     * Normalize value to avoid floating-point artifacts while preserving user intent.
     */
    private normalizeValue(value: number): number {
        const clamped = this.clampValue(value);
        const step = this.numericStep();

        if (!Number.isFinite(clamped) || !Number.isFinite(step) || step === 0) {
            return clamped;
        }

        // Round only to the step precision, do not snap to the step multiple.
        const precision = this.getStepPrecision(step);
        return Number(clamped.toFixed(precision));
    }

    /**
     * Derive the number of decimal places required by a step value.
     */
    private getStepPrecision(step: number): number {
        const stepText = step.toString().toLowerCase();

        if (stepText.includes('e-')) {
            const exp = stepText.split('e-')[1];
            return Number.parseInt(exp ?? '0', 10);
        }

        const dotIndex = stepText.indexOf('.');
        return dotIndex >= 0 ? stepText.length - dotIndex - 1 : 0;
    }
}
